"use client"

import { sanitizeDigitsInput } from "@/lib/auth/contact-input"

import * as React from "react"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from "lucide-react"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { SimpleSelect } from "@/components/ui/select"

interface DataTablePaginationProps {
  pageIndex: number
  pageCount: number
  pageSize: number
  filteredTotal: number
  pageSizeOptions?: number[]
  showPageSizeSelector?: boolean
  showPageJumpInput?: boolean
  disabled?: boolean
  onPageIndexChange: (pageIndex: number) => void
  onPageSizeChange: (pageSize: number) => void
}

export function DataTablePagination({
  pageIndex,
  pageCount,
  pageSize,
  filteredTotal,
  pageSizeOptions,
  showPageSizeSelector = false,
  showPageJumpInput = false,
  disabled = false,
  onPageIndexChange,
  onPageSizeChange,
}: DataTablePaginationProps) {
  const t = useTranslations("site.ui")
  const [jumpPageDraft, setJumpPageDraft] = React.useState<string | null>(null)
  const canPreviousPage = pageIndex > 0
  const canNextPage = pageIndex < pageCount - 1

  const pageSizeChoices = React.useMemo(() => {
    const allOptions = [...(pageSizeOptions ?? []), pageSize]
      .filter((value) => Number.isFinite(value) && value > 0)
      .map((value) => Math.trunc(value))
    return Array.from(new Set(allOptions)).sort((a, b) => a - b)
  }, [pageSize, pageSizeOptions])

  React.useEffect(() => {
    setJumpPageDraft(null)
  }, [pageIndex])

  function applyPageJump() {
    if (jumpPageDraft === null) return
    const raw = jumpPageDraft ?? String(pageIndex + 1)
    const parsed = Number.parseInt(raw, 10)
    if (Number.isNaN(parsed)) {
      setJumpPageDraft(null)
      return
    }
    const target = Math.min(Math.max(parsed, 1), Math.max(1, pageCount)) - 1
    if (target !== pageIndex) onPageIndexChange(target)
    setJumpPageDraft(null)
  }

  function keepInputFocus(event: React.MouseEvent) {
    event.preventDefault()
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {!showPageSizeSelector && (
        <span className="text-sm text-muted-foreground">
          {t("total", { count: filteredTotal })}
        </span>
      )}
      {showPageSizeSelector && pageSizeChoices.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>{t("total", { count: filteredTotal })}</span>
          <span>{t("perPage")}</span>
          <SimpleSelect
            side="top"
            value={String(pageSize)}
            disabled={disabled}
            onValueChange={(value) => onPageSizeChange(Number(value))}
            size="sm"
            className="w-[64px] gap-1 px-1.5"
            contentClassName="min-w-0"
            options={pageSizeChoices.map((value) => ({
              value: String(value),
              label: value,
            }))}
          />
        </div>
      )}
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label={t("firstPage")}
          onMouseDown={keepInputFocus}
          onClick={() => onPageIndexChange(0)}
          disabled={disabled || !canPreviousPage}
        >
          <ChevronsLeftIcon />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label={t("previousPage")}
          onMouseDown={keepInputFocus}
          onClick={() => onPageIndexChange(pageIndex - 1)}
          disabled={disabled || !canPreviousPage}
        >
          <ChevronLeftIcon />
        </Button>
        {showPageJumpInput ? (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Input
              sanitizeValue={sanitizeDigitsInput}
              value={jumpPageDraft ?? String(pageIndex + 1)}
              disabled={disabled}
              onChange={(event) => setJumpPageDraft(event.target.value.replace(/\D/g, ""))}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  applyPageJump()
                }
              }}
              className="h-7 w-12 px-1 text-center text-sm"
              onBlur={applyPageJump}
              aria-label={t("currentPage")}
              inputMode="numeric"
            />
            <span>/ {pageCount}</span>
          </div>
        ) : (
          <span className="min-w-[72px] px-1 text-center text-sm text-muted-foreground">
            {pageIndex + 1} / {pageCount}
          </span>
        )}
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label={t("nextPage")}
          onMouseDown={keepInputFocus}
          onClick={() => onPageIndexChange(pageIndex + 1)}
          disabled={disabled || !canNextPage}
        >
          <ChevronRightIcon />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label={t("lastPage")}
          onMouseDown={keepInputFocus}
          onClick={() => onPageIndexChange(pageCount - 1)}
          disabled={disabled || !canNextPage}
        >
          <ChevronsRightIcon />
        </Button>
      </div>
    </div>
  )
}
